import { z } from "zod";

import { buildNamespace } from "@smart/itops-types-basic";
import { convertUTCStringToZonedString } from "@smart/itops-utils-basic";

import { system } from "./base";

export const SettingNS = buildNamespace({
  system,
  entity: "Setting",
} as const);

export const SettingSchema = z.object({
  uri: SettingNS.schema,
  operationId: z.string(),

  teamUri: z.string(),
  defaultClientMessage: z.object({
    matter: z.string().optional(),
    lead: z.string().optional(),
    reminder: z.string().optional(),
    confirmation: z.string().optional(),
    redirectUrl: z.string().optional(),
  }),
  locations: z.array(z.string()).optional(),
  areasOfLaw: z.array(z.string()).optional(),
  disableGlobalForms: z.boolean().optional(),

  staffToNotify: z
    .object({
      creator: z.boolean().optional(),
      personResponsible: z.boolean().optional(),
      personAssisting: z.boolean().optional(),
      otherStaffIds: z.array(z.string()).optional(),
    })
    .optional(),

  branding: z
    .object({
      brandColor: z.string().optional(),
      affectedAreas: z
        .object({
          headerBackground: z.boolean().optional(),
          headerUnderline: z.boolean().optional(),
          firmName: z.boolean().optional(),
          sectionTabHighlights: z.boolean().optional(),
          sectionHeadings: z.boolean().optional(),
          buttons: z.boolean().optional(),
        })
        .optional(),
      headerTextColor: z.string().optional(),
      backgroundColor: z.string().optional(),
      headerDisplay: z
        .object({
          firmLogo: z.boolean().optional(),
          firmName: z.boolean().optional(),
        })
        .optional(),
      buttonRadius: z.string().optional(),
      inputRadius: z.string().optional(),
      logo: z.string().optional(),
    })
    .optional(),

  voiceGuide: z
    .object({
      enabled: z.boolean().optional(),
    })
    .optional(),

  reminder: z
    .object({
      enabled: z.boolean(),
      effectiveFrom: z.string().optional(),
      firstReminderDays: z.number().optional(),
      timezone: z.string().optional(),
      stopOnManualSend: z.boolean().optional(),
      sendFollowUps: z.boolean().optional(),
      repeatEveryDays: z.number().optional(),
      maxFollowUps: z.number().optional(),
    })
    .optional(),

  updatedAt: z.string(),
});

// reminders are queued once per weekday by the hourly scan tick
// that falls within this hour of the firm's local time
export const REMINDER_SEND_HOUR = 9;

export const isWeekday = (date: string) => {
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
  return weekday !== 0 && weekday !== 6;
};

export const nextWeekday = (date: string) => {
  const next = new Date(`${date}T00:00:00Z`);
  do {
    next.setUTCDate(next.getUTCDate() + 1);
  } while (next.getUTCDay() === 0 || next.getUTCDay() === 6);
  return next.toISOString().slice(0, 10);
};

// a reminder not sent yet goes out at the next weekday scan
// which runs at the start of the send hour
export const reminderSendDate = (
  dueDate: string,
  timezone: string,
  now: string,
) => {
  const zonedNow = convertUTCStringToZonedString(now, timezone);
  const today = zonedNow.slice(0, 10);
  const nextScanDate =
    isWeekday(today) && Number(zonedNow.slice(11, 13)) < REMINDER_SEND_HOUR
      ? today
      : nextWeekday(today);

  return dueDate > nextScanDate ? dueDate : nextScanDate;
};
